import { useState, useEffect } from 'react';
import { BuildingOfficeIcon, PlusIcon, PencilIcon, ArrowPathIcon, NoSymbolIcon, CheckCircleIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { Empresa } from '../../types';
import EmpresaModal from './EmpresaModal';
import { useLoading } from '../../contexts/LoadingContext';

interface EmpresasProps {
  initialEmpresas: Empresa[];
  onEmpresasChange: (empresas: Empresa[]) => void;
  onSelectEmpresa: (empresa: Empresa) => void;
  onNavigate?: (section: string) => void;
  session?: { usuario: { role?: string; empresaId?: string; } };
  planoInfo?: any;
}

const Empresas: React.FC<EmpresasProps> = ({ 
  initialEmpresas,
  onEmpresasChange,
  onSelectEmpresa,
  onNavigate,
  session,
  planoInfo
}) => {
  console.log('🏢 Empresas component - initialEmpresas:', initialEmpresas);
  
  const { loading } = useLoading();
  const safeInitialEmpresas = initialEmpresas || [];
  
  const [empresas, setEmpresas] = useState<Empresa[]>(safeInitialEmpresas);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null);
  const [deletingEmpresa, setDeletingEmpresa] = useState<Empresa | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const isLoadingEmpresas = loading.isLoading && (loading.type === 'empresas' || loading.type === 'mongodb');

  // Limites do plano
  const limiteEmpresas = planoInfo?.limites?.empresas ?? 999;
  const mostrarLimite = limiteEmpresas < 999;
  const trialExpirado = planoInfo?.trial_expirado === true;

  useEffect(() => {
    setEmpresas(initialEmpresas || []);
  }, [initialEmpresas]);

  // Permissão: admin, administrator ou company_admin podem gerenciar
  const userRole = session?.usuario?.role || '';
  const isAdmin = ['admin', 'administrator', 'company_admin'].includes(userRole.toLowerCase());

  // A lista `empresas` já vem escopada pelo backend (empresas que o usuário pode acessar:
  // admin do grupo OU colaborador). Não re-filtrar por empresaId aqui — isso escondia as
  // sub-empresas para editores/usuários comuns (ex.: equipe do DP só via a Refricril).
  const empresasParaExibir = (empresas || []).filter(empresa => empresa && empresa.id);

  const usoEmpresas = planoInfo?.uso?.empresas ?? empresasParaExibir.length;
  const limiteAtingido = usoEmpresas >= limiteEmpresas;

  const handleAddEmpresa = (newEmpresa: Omit<Empresa, 'id' | 'createdAt' | 'updatedAt'>) => {
    const empresa: Empresa = {
      id: Date.now().toString(),
      ...newEmpresa,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    const updatedEmpresas = [...empresas, empresa];
    setEmpresas(updatedEmpresas);
    onEmpresasChange(updatedEmpresas);
    setModalOpen(false);
  };

  const handleEditEmpresa = (updatedEmpresa: Empresa) => {
    const updatedEmpresas = empresas.map(emp => 
      emp.id === updatedEmpresa.id ? { ...updatedEmpresa, updatedAt: new Date() } : emp
    );
    setEmpresas(updatedEmpresas);
    onEmpresasChange(updatedEmpresas);
    setEditingEmpresa(null);
    setModalOpen(false);
  };

  const handleDeleteEmpresa = async () => {
    if (!deletingEmpresa) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.usuario) {
        const u = session.usuario as any;
        if (u.email) headers['X-User-Email'] = u.email;
        if (u.role) headers['X-User-Role'] = u.role;
        if (u.empresaId) headers['X-Empresa-Id'] = u.empresaId;
      }
      const resp = await fetch(`/api/empresas/${deletingEmpresa.id}`, { method: 'DELETE', headers });
      const data = await resp.json();
      if (data.success) {
        const updated = empresas.filter(e => e.id !== deletingEmpresa.id);
        setEmpresas(updated);
        onEmpresasChange(updated);
        setDeletingEmpresa(null);
      } else {
        setDeleteError(data.error || 'Erro ao excluir empresa.');
      }
    } catch (err) {
      setDeleteError('Erro de conexão ao tentar excluir a empresa.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = (empresa: Empresa) => {
    const novoStatus = empresa.status === 'ativo' ? 'inativo' : 'ativo';
    const label = novoStatus === 'inativo' ? 'inativar' : 'reativar';
    if (!window.confirm(`Deseja ${label} a empresa "${empresa.nome}"?`)) return;
    
    const updated = { ...empresa, status: novoStatus, updatedAt: new Date() };
    const updatedEmpresas = empresas.map(emp => emp.id === empresa.id ? updated : emp);
    setEmpresas(updatedEmpresas);
    onEmpresasChange(updatedEmpresas);
  };

  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Empresas</h1>
            <p className="text-xs text-gray-500">Gerencie as empresas do grupo</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              {mostrarLimite && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                  limiteAtingido
                    ? 'bg-red-50 text-red-600 border-red-200'
                    : usoEmpresas >= limiteEmpresas - 1
                    ? 'bg-amber-50 text-amber-600 border-amber-200'
                    : 'bg-indigo-50 text-indigo-600 border-indigo-200'
                }`}>
                  {usoEmpresas}/{limiteEmpresas} empresas
                </span>
              )}
              <button
                onClick={() => {
                  if (limiteAtingido || trialExpirado) {
                    setShowUpgradeModal(true);
                  } else {
                    setEditingEmpresa(null);
                    setModalOpen(true);
                  }
                }}
                disabled={trialExpirado}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  limiteAtingido || trialExpirado
                    ? 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                    : 'text-white bg-indigo-600 hover:bg-indigo-700'
                }`}
                title={limiteAtingido ? `Limite de ${limiteEmpresas} empresas atingido` : trialExpirado ? 'Trial expirado' : ''}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Nova Empresa
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoadingEmpresas ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <div className="max-w-sm mx-auto">
                <div className="flex justify-center mb-4">
                  <ArrowPathIcon className="h-10 w-10 text-indigo-500 animate-spin" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Carregando empresas</h3>
                <p className="text-sm text-gray-500">
                  {loading.submessage || 'Buscando empresas no sistema...'}
                </p>
              </div>
            </div>
          ) : empresasParaExibir.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <div className="max-w-sm mx-auto">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                  <BuildingOfficeIcon className="h-7 w-7 text-gray-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Nenhuma empresa cadastrada</h3>
                <p className="text-sm text-gray-500 mb-5">Comece cadastrando sua primeira empresa.</p>
                {isAdmin && (
                  <button
                    onClick={() => { setEditingEmpresa(null); setModalOpen(true); }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm shadow-sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Cadastrar Empresa
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-4">
              {empresasParaExibir.map((empresa) => (
                <div
                  key={empresa.id}
                  className={`bg-white rounded-xl border p-4 hover:shadow-sm transition-all ${
                    empresa.status === 'inativo'
                      ? 'border-red-200 opacity-70'
                      : 'border-gray-200 hover:border-indigo-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{empresa.nome || 'Nome não informado'}</h3>
                      <p className="text-sm text-gray-600">{empresa.razaoSocial || 'Razão social não informada'}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 ml-3">
                        <button
                          onClick={() => { setEditingEmpresa(empresa); setModalOpen(true); }}
                          className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                          title="Editar empresa"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(empresa)}
                          className={`p-2 rounded-lg transition-colors ${
                            empresa.status === 'ativo'
                              ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                              : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                          }`}
                          title={empresa.status === 'ativo' ? 'Inativar empresa' : 'Reativar empresa'}
                        >
                          {empresa.status === 'ativo' ? (
                            <NoSymbolIcon className="h-4 w-4" />
                          ) : (
                            <CheckCircleIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => { setDeleteError(null); setDeletingEmpresa(empresa); }}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                          title="Excluir empresa"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                      <span className="text-gray-500">CNPJ:</span>
                      <span className="text-gray-900 font-medium">{empresa.cnpj ? formatCNPJ(empresa.cnpj) : 'Não informado'}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                      <span className="text-gray-500">Email:</span>
                      <span className="text-gray-900 font-medium truncate sm:ml-2 max-w-full">{empresa.email || 'Não informado'}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                      <span className="text-gray-500">Telefone:</span>
                      <span className="text-gray-900 font-medium">{empresa.telefone || 'Não informado'}</span>
                    </div>
                    {empresa.endereco && (
                      <div>
                        <span className="text-gray-500">Endereço:</span>
                        <div className="mt-1 text-gray-900 text-xs leading-relaxed break-words">
                          {empresa.endereco.rua && empresa.endereco.numero ? (
                            <>
                              {`${empresa.endereco.rua}, ${empresa.endereco.numero}${empresa.endereco.complemento ? ` - ${empresa.endereco.complemento}` : ''}`}<br />
                              {`${empresa.endereco.bairro || ''}, ${empresa.endereco.cidade || ''} - ${empresa.endereco.estado || ''}`}<br />
                              {`CEP: ${empresa.endereco.cep || ''}`}
                            </>
                          ) : (
                            'Não informado'
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                        empresa.status === 'ativo' 
                          ? 'bg-emerald-50 text-emerald-700' 
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {empresa.status === 'ativo' ? 'Ativa' : 'Inativa'}
                      </span>
                      <div className="flex gap-2">
                        {/* Só mostra "Completar cadastro" se faltar algum campo importante */}
                        {(!empresa.cnpj || !empresa.email || !empresa.telefone || !empresa.razaoSocial || 
                          !empresa.endereco?.rua || !empresa.endereco?.numero || !empresa.endereco?.cidade || !empresa.endereco?.estado || !empresa.endereco?.cep) && (
                        <button
                          onClick={() => { setEditingEmpresa(empresa); setModalOpen(true); }}
                          className="px-3 py-1 bg-white text-indigo-600 border border-indigo-200 text-xs rounded-md hover:bg-indigo-50 transition-colors font-medium"
                        >
                          Completar cadastro
                        </button>
                        )}
                        <button
                          onClick={() => {
                            try {
                              onSelectEmpresa(empresa);
                            } catch (error) {
                              console.error('❌ Erro ao selecionar empresa:', error);
                            }
                          }}
                          className="px-3 py-1 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700 transition-colors font-medium"
                        >
                          Selecionar
                        </button>
                      </div>
                    </div>
                    <div className="mt-1.5 text-[10px] text-gray-400">
                      Atualizado em: {new Date(empresa.updatedAt).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {deletingEmpresa && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <TrashIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Excluir empresa</h3>
                  <p className="text-xs text-gray-500">Esta ação não pode ser desfeita.</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-1">
                Deseja excluir a empresa <span className="font-semibold">"{deletingEmpresa.nome}"</span>?
              </p>
              <p className="text-xs text-gray-500 mb-4">A exclusão só é permitida se não houver dados vinculados (fluxos, colaboradores, departamentos, etc.).</p>
              {deleteError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700">{deleteError}</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setDeletingEmpresa(null); setDeleteError(null); }}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteEmpresa}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center gap-1.5"
                >
                  {isDeleting ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <TrashIcon className="h-3.5 w-3.5" />}
                  {isDeleting ? 'Excluindo...' : 'Confirmar exclusão'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Upgrade */}
        {showUpgradeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
              {/* Header com gradiente Escalone */}
              <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 px-6 py-8 text-center relative overflow-hidden">
                {/* Decorações de fundo */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                
                {/* Logo Escalone */}
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-extrabold text-xl shadow-lg">
                      E
                    </div>
                    <span className="font-extrabold text-2xl text-white tracking-tight">Escalone</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">Você atingiu o limite</h3>
                  <p className="text-indigo-200 text-sm">
                    Seu plano permite até {limiteEmpresas} empresa{limiteEmpresas > 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="px-6 py-6">
                <p className="text-sm text-gray-600 text-center mb-5">
                  Para criar mais empresas e desbloquear todo o potencial do Escalone, conheça nossos planos.
                </p>

                {/* Cards de uso */}
                <div className="grid grid-cols-2 gap-2.5 mb-6">
                  {[
                    { label: 'Empresas', uso: planoInfo?.uso?.empresas ?? 0, limite: planoInfo?.limites?.empresas ?? 0, icon: '🏢' },
                    { label: 'Fluxos', uso: planoInfo?.uso?.fluxos ?? 0, limite: planoInfo?.limites?.fluxos ?? 0, icon: '⚡' },
                    { label: 'Colaboradores', uso: planoInfo?.uso?.colaboradores ?? 0, limite: planoInfo?.limites?.colaboradores ?? 0, icon: '👥' },
                    { label: 'Automações', uso: planoInfo?.uso?.automacoes ?? 0, limite: planoInfo?.limites?.automacoes ?? 0, icon: '🤖' },
                  ].map(item => {
                    const pct = Math.min(100, (item.uso / Math.max(item.limite, 1)) * 100);
                    const atLimit = item.uso >= item.limite;
                    const nearLimit = item.uso >= item.limite * 0.8;
                    return (
                      <div key={item.label} className={`rounded-xl p-3 border ${
                        atLimit ? 'bg-red-50/50 border-red-200' : 'bg-gray-50 border-gray-100'
                      }`}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-sm">{item.icon}</span>
                          <span className="text-xs font-semibold text-gray-700">{item.label}</span>
                        </div>
                        <div className="flex items-baseline gap-1 mb-1.5">
                          <span className={`text-lg font-extrabold ${atLimit ? 'text-red-600' : 'text-gray-900'}`}>{item.uso}</span>
                          <span className="text-xs text-gray-400">/ {item.limite}</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              atLimit ? 'bg-red-500' : nearLimit ? 'bg-amber-400' : 'bg-indigo-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pro highlight */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 mb-5 border border-indigo-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">Plano Pro</p>
                      <p className="text-xs text-gray-500">Empresas, fluxos, colaboradores e automações <span className="font-semibold text-indigo-600">ilimitados</span></p>
                    </div>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowUpgradeModal(false)}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Agora não
                  </button>
                  <button
                    onClick={() => { setShowUpgradeModal(false); onNavigate?.('planos'); }}
                    className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md shadow-indigo-500/25 flex items-center justify-center gap-1.5"
                  >
                    Ver planos
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {modalOpen && (
          <EmpresaModal
            empresa={editingEmpresa}
            onSave={(empresa) => {
              if (editingEmpresa) {
                handleEditEmpresa(empresa as Empresa);
              } else {
                handleAddEmpresa(empresa as Omit<Empresa, 'id' | 'createdAt' | 'updatedAt'>);
              }
            }}
            onClose={() => {
              setModalOpen(false);
              setEditingEmpresa(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Empresas;